import { useMemo } from "react";

import { LOINC } from "@medplum/core";
import { QuestionnaireItem } from "@medplum/fhirtypes";
import { Container } from "@medplum/react";

import { Slider } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import { TextWrapper } from "@/components/TextWrapper";

type QuestionnaireSliderProps<T> = {
  label?: string;
  name: string;
  form: UseFormReturnType<T>;
  group: QuestionnaireItem | undefined;
};

const INITIAL_VALUE = {
  system: LOINC,
  code: "LA6111-4",
  display: "0",
};

const QuestionnaireSlider = <T,>({ form, group, name, label }: QuestionnaireSliderProps<T>) => {
  const { value, onChange } = form.getInputProps(name);

  const parsedValue = useMemo(() => {
    if (!value) {
      onChange(() => INITIAL_VALUE);
      return;
    }

    if ("valueCoding" in value) {
      return Number(value.valueCoding.display);
    } else if ("display" in value) {
      return Number(value.display);
    }
  }, [onChange, value]);

  const maxValue = group?.answerOption?.length ? group?.answerOption?.length - 1 : 0;

  const marks = group?.answerOption?.map((item) => ({
    value: Number(item.valueCoding?.display),
    label: item.valueCoding?.display,
  }));

  const parseValueToCodeableConcept = (value: number) => {
    const item = group?.answerOption?.find(
      (option) => option.valueCoding?.display === value.toString(),
    );

    return item?.valueCoding;
  };

  return (
    <Container p={0} mb="md" w="100%">
      <TextWrapper mb="md">{label}</TextWrapper>
      <Slider
        w="100%"
        name={label}
        title={label}
        marks={marks}
        max={maxValue}
        aria-label={label}
        styles={{
          mark: { display: "none" },
          markLabel: { fontSize: 12, marginTop: 5, fontWeight: "bold", color: "black" },
        }}
        {...form.getInputProps(name)}
        value={parsedValue}
        onChange={(value) => onChange(() => parseValueToCodeableConcept(value))}
      />
    </Container>
  );
};

export default QuestionnaireSlider;
